/**
 * Shared Xero-workspace primitives — extracted from the original
 * XeroAccountingCards so the COGS / SIT cards and the workspace tabs share one
 * source of truth for journal links, status badges, stat tiles and the
 * account-code picker. Pure presentational helpers; no page-level state.
 */
import { ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { XERO_JOURNAL_URL } from '../lib/links';

export function StatTile({
  label,
  value,
  foot,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  foot?: string;
  tone?: 'neutral' | 'emerald' | 'amber';
}) {
  return (
    <div className="rounded-md border px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          'text-sm font-semibold tabular-nums',
          tone === 'emerald' && 'text-emerald-700',
          tone === 'amber' && 'text-amber-700'
        )}
      >
        {value}
      </p>
      {foot && <p className="text-[11px] text-muted-foreground tabular-nums">{foot}</p>}
    </div>
  );
}

export function JournalStatusBadge({ status }: { status: string }) {
  if (status === 'success') {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300/80 border-0 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide hover:bg-emerald-100">
        Posted
      </Badge>
    );
  }
  if (status === 'failed') {
    return (
      <Badge
        variant="destructive"
        className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      >
        Failed
      </Badge>
    );
  }
  if (status === 'skipped_locked_period') {
    return (
      <Badge className="bg-amber-100 text-amber-800 ring-1 ring-amber-300/80 border-0 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide hover:bg-amber-100">
        Skipped locked period
      </Badge>
    );
  }
  return (
    <Badge
      variant="secondary"
      className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
    >
      Pending
    </Badge>
  );
}

export function JournalLink({
  journalId,
  journalNumber,
}: {
  journalId: string | null;
  journalNumber: string | null;
}) {
  if (!journalId) return <span className="text-muted-foreground">-</span>;
  return (
    <a
      href={`${XERO_JOURNAL_URL}${journalId}`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-emerald-700 hover:underline"
    >
      {journalNumber ?? 'Journal'}
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
}

export function AccountCodeSelect({
  id,
  value,
  onChange,
  accounts,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (code: string) => void;
  accounts: Array<{ id: string; account_code: string; name: string }>;
  disabled?: boolean;
}) {
  const inList = accounts.some((account) => account.account_code === value);
  return (
    <Select value={value || undefined} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className="h-8 text-sm">
        <SelectValue placeholder="Choose account" />
      </SelectTrigger>
      <SelectContent>
        {accounts.map((account) => (
          <SelectItem key={account.id} value={account.account_code}>
            {account.account_code} · {account.name}
          </SelectItem>
        ))}
        {/* The saved code may not be dropdown-visible; keep it renderable. */}
        {value && !inList && <SelectItem value={value}>{value}</SelectItem>}
      </SelectContent>
    </Select>
  );
}
